import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Tag, Globe, ExternalLink } from "lucide-react";

interface Template {
  id: string;
  title: string;
  description: string;
  category: string;
  previewUrl: string;
  thumbnailUrl: string;
  technologies: string[];
  created_at: string;
}

const TEMPLATES: Template[] = [
  {
    id: "1",
    title: "Animated 3D Forms",
    description:
      "Animated 3D Forms is a futuristic, responsive login and signup page with smooth transitions.",
    category: "Login Form",
    previewUrl: "https://3d-rxstech.vercel.app/",
    thumbnailUrl: "https://i.imgur.com/jb84FT6.png",
    technologies: ["HTML", "CSS", "JavaScript"],
    created_at: "2025-02-22",
  },
  // {
  //   id: "2",
  //   title: "Corporate Business Template",
  //   description:
  //     "Professional and modern company profile template with sleek animations.",
  //   category: "Company Profile",
  //   previewUrl: "https://example.com/corporate-preview",
  //   thumbnailUrl:
  //     "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000",
  //   technologies: ["React", "Tailwind CSS", "TypeScript"],
  //   created_at: "2024-02-14",
  // },
  // {
  //   id: "3",
  //   title: "E-commerce Starter",
  //   description:
  //     "Feature-rich e-commerce template with cart functionality and product showcase.",
  //   category: "E-commerce",
  //   previewUrl: "https://example.com/ecommerce-preview",
  //   thumbnailUrl:
  //     "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&q=80&w=1000",
  //   technologies: ["React", "Tailwind CSS", "Redux"],
  //   created_at: "2024-02-13",
  // },
  // {
  //   id: "4",
  //   title: "Landing Page Template",
  //   description:
  //     "A sleek and modern landing page template designed for startups and businesses.",
  //   category: "Landing Page",
  //   previewUrl: "https://example.com/landing-preview",
  //   thumbnailUrl:
  //     "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1000",
  //   technologies: ["Next.js", "Tailwind CSS"],
  //   created_at: "2024-02-12",
  // },
  // {
  //   id: "5",
  //   title: "Blog Template",
  //   description:
  //     "A content-rich blog template with easy customization and SEO optimization.",
  //   category: "Blog",
  //   previewUrl: "https://example.com/blog-preview",
  //   thumbnailUrl:
  //     "https://images.unsplash.com/photo-1521747116042-5a810fda9664?auto=format&fit=crop&q=80&w=1000",
  //   technologies: ["Gatsby", "Styled Components"],
  //   created_at: "2024-02-11",
  // },
  // {
  //   id: "6",
  //   title: "Dashboard Template",
  //   description:
  //     "A fully responsive admin dashboard template with interactive charts and tables.",
  //   category: "Dashboard",
  //   previewUrl: "https://example.com/dashboard-preview",
  //   thumbnailUrl:
  //     "https://images.unsplash.com/photo-1587614296815-0a84bbaae854?auto=format&fit=crop&q=80&w=1000",
  //   technologies: ["React", "Tailwind CSS", "D3.js"],
  //   created_at: "2024-02-10",
  // },
];

const CATEGORIES = [
  "All",
  "Login Form",
  "Portfolio",
  "Company Profile",
  "Landing Page",
  "Blog",
];

export default function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredTemplates = TEMPLATES.filter((template) => {
    const matchesSearch =
      template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4 dark:text-white">
          Premium Website Templates
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Browse our collection of professionally crafted website templates.
          Each template is built with modern technologies and best practices.
        </p>
      </div>

      <div className="mb-8">
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:gap-4">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search templates..."
              className="input pl-10 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`btn ${
                  selectedCategory === category
                    ? "btn-primary"
                    : "btn-secondary"
                }`}
              >
                <Globe className="inline-block h-4 w-4 mr-1" />
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredTemplates.map((template) => (
          <Link
            key={template.id}
            to={`/code/${template.id}`}
            className="group block rounded-xl overflow-hidden border dark:border-gray-700 hover:shadow-xl transition-all duration-300 dark:bg-gray-800"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={template.thumbnailUrl}
                alt={template.title}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <span className="btn btn-primary opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Preview Template
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-xl font-semibold dark:text-white">
                  {template.title}
                </h2>
                <span className="px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {template.category}
                </span>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                {template.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {template.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="px-2 py-1 text-sm rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
